/**
 * Invoice API utilities
 * Shared API helper and constants for invoicing components
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * API helper with authentication
 * @param {string} endpoint - API endpoint (e.g., '/api/invoicing/invoices')
 * @param {object} options - Fetch options (method, body, headers)
 * @returns {Promise<object>} - JSON response
 */
export const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('ebb_token');
  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'API Error');
  }
  return response.json();
};

/**
 * Download invoice PDF
 * @param {string} invoiceId - Invoice ID
 * @param {string} invoiceNumber - Invoice number for filename
 */
export const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
  const token = localStorage.getItem('ebb_token');
  const response = await fetch(`${BACKEND_URL}/api/invoicing/invoices/${invoiceId}/pdf`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${invoiceNumber}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * VAT calculation helper - TTC is the ABSOLUTE source of truth
 * This ensures €80.00 TTC always stays €80.00 TTC (no rounding errors)
 * HTVA and TVA are derived for display only
 * 
 * @param {number} priceTTC - Tax-inclusive price
 * @param {number} vatRate - VAT rate (default 21%)
 * @returns {object} - { htva, tva, ttc }
 */
export const getVATBreakdown = (priceTTC, vatRate = 21) => {
  const ttc = Math.round(priceTTC * 100) / 100; // Ensure TTC is clean
  const htva = Math.floor(ttc / (1 + vatRate / 100) * 100) / 100;
  const tva = Math.round((ttc - htva) * 100) / 100;
  return { htva, tva, ttc };
};

/**
 * Translations for invoicing components
 */
export const translations = {
  fr: {
    title: 'Facturation',
    invoices: 'Factures',
    templates: 'Templates',
    clients: 'Clients',
    settings: 'Paramètres',
    newInvoice: 'Nouvelle facture',
    newCreditNote: 'Nouvelle note de crédit',
    editInvoice: 'Modifier la facture',
    invoiceNumber: 'Numéro',
    date: 'Date',
    dueDate: 'Échéance',
    customer: 'Client',
    amount: 'Montant',
    status: 'Statut',
    actions: 'Actions',
    search: 'Rechercher...',
    draft: 'Brouillon',
    sent: 'Envoyée',
    paid: 'Payée',
    partial: 'Partiel',
    cancelled: 'Annulée',
    overdue: 'En retard',
    download: 'Télécharger PDF',
    edit: 'Modifier',
    delete: 'Supprimer',
    duplicate: 'Dupliquer',
    createCreditNote: 'Créer note de crédit',
    addPayment: 'Ajouter paiement',
    addReminder: 'Ajouter rappel',
    totalInvoiced: 'Total facturé',
    totalPaid: 'Total payé',
    totalDue: 'Total dû',
    save: 'Enregistrer',
    cancel: 'Annuler',
    close: 'Fermer',
    subtotalHTVA: 'Sous-total HTVA',
    vat: 'TVA',
    totalTTC: 'Total TTC',
    addItem: 'Ajouter un article',
    description: 'Description',
    quantity: 'Quantité',
    unitPrice: 'Prix unitaire',
    vatRate: 'Taux TVA',
    lineTotal: 'Total ligne',
    notes: 'Notes',
    customerInfo: 'Informations client',
    name: 'Nom',
    email: 'Email',
    address: 'Adresse',
    postalCode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    vatNumber: 'N° TVA',
    phone: 'Téléphone',
    template: 'Template',
    language: 'Langue',
    dueDays: 'Jours avant échéance',
    sendEmail: 'Envoyer par email',
    sendReminder: 'Envoyer rappel',
    selectProduct: 'Sélectionner un produit',
    manualEntry: 'Saisie manuelle',
    selectClient: 'Sélectionner un client',
    newClient: 'Nouveau client',
  },
  nl: {
    title: 'Facturatie',
    invoices: 'Facturen',
    templates: 'Sjablonen',
    clients: 'Klanten',
    settings: 'Instellingen',
    newInvoice: 'Nieuwe factuur',
    newCreditNote: 'Nieuwe creditnota',
    editInvoice: 'Factuur bewerken',
    invoiceNumber: 'Nummer',
    date: 'Datum',
    dueDate: 'Vervaldatum',
    customer: 'Klant',
    amount: 'Bedrag',
    status: 'Status',
    actions: 'Acties',
    search: 'Zoeken...',
    draft: 'Concept',
    sent: 'Verzonden',
    paid: 'Betaald',
    partial: 'Gedeeltelijk',
    cancelled: 'Geannuleerd',
    overdue: 'Achterstallig',
    download: 'PDF downloaden',
    edit: 'Bewerken',
    delete: 'Verwijderen',
    duplicate: 'Dupliceren',
    createCreditNote: 'Creditnota aanmaken',
    addPayment: 'Betaling toevoegen',
    addReminder: 'Herinnering toevoegen',
    totalInvoiced: 'Totaal gefactureerd',
    totalPaid: 'Totaal betaald',
    totalDue: 'Totaal verschuldigd',
    save: 'Opslaan',
    cancel: 'Annuleren',
    close: 'Sluiten',
    subtotalHTVA: 'Subtotaal excl. BTW',
    vat: 'BTW',
    totalTTC: 'Totaal incl. BTW',
    addItem: 'Artikel toevoegen',
    description: 'Omschrijving',
    quantity: 'Aantal',
    unitPrice: 'Eenheidsprijs',
    vatRate: 'BTW-tarief',
    lineTotal: 'Regeltotaal',
    notes: 'Notities',
    customerInfo: 'Klantgegevens',
    name: 'Naam',
    email: 'E-mail',
    address: 'Adres',
    postalCode: 'Postcode',
    city: 'Stad',
    country: 'Land',
    vatNumber: 'BTW-nummer',
    phone: 'Telefoon',
    template: 'Sjabloon',
    language: 'Taal',
    dueDays: 'Dagen tot vervaldatum',
    sendEmail: 'Per e-mail verzenden',
    sendReminder: 'Herinnering verzenden',
    selectProduct: 'Selecteer een product',
    manualEntry: 'Handmatige invoer',
    selectClient: 'Selecteer een klant',
    newClient: 'Nieuwe klant',
  },
};

export default { apiCall, downloadInvoicePdf, getVATBreakdown, translations };
